import {
	createColumnHelper,
	DisplayColumnDef
} from "@tanstack/react-table";
import { SBTableColumn } from "../../models/TableColumn";
import { SBEditableColumn } from "../SBEditableColumn";
import { buildSelectColumn } from "./buildSelectColumn";
import { fetchColumnSize } from "./fetchColumnSize";
import { SBTranslate } from "i18n";

export function columnsAdapter<T>({
	columns,
	rows,
	hasSelection,
}: ColumnsAdapterProps<T>) {
	const columnHelper = createColumnHelper<T>();
	const minSizes = fetchColumnSize({ columns, rows });

	const selectableColumn = hasSelection ? [buildSelectColumn()] : [];

	const firstRenderedGroup = columns.findIndex((c) =>
		(c.headers ?? []).some((h) => !h.tableHide),
	);

	return columns.map((column, index: number) => {
		let columns = column.headers?.map((header) => {
			return columnHelper.accessor(header.id, {
				id: header?.key,
				header: header.name,
				size: minSizes.get(header.key!),
				tip: header.tip,
				align: header.align,
				fixed: header.fixed,
				disabled: header.disabled,
				styleFn: header.styleFn,
				prepend: header.prepend,
				maxSize: header.maxWidth,
				filterFn: header.filterFn ?? "fuzzy" as any,
				enableHiding: header.hide,
				enableGrouping: header.enableGrouping,
				groupByTip: header.groupByTip,
				aggregationFn: header.aggregationFn,
				aggregatedCell: header.aggregatedCell,
				hideToggle: header.hideToggle,
				cell: (info: any) => {
					const row = info?.row?.original;
					const isSubRow = info.row.depth > 0;
					const activeHeader = (isSubRow && header.child) ? header.child : header;

					if (activeHeader.editable) {
						const value = (isSubRow && header.child)
							? (typeof header.child.id === "function" ? header.child.id(row) : row[header.child.id as any])
							: info.getValue();

						const overrideInfo = {
							...info,
							getValue: () => value,
						};

						const funcStyle = activeHeader?.styleFn
							? activeHeader?.styleFn(row)
							: undefined;

						return (
							<SBEditableColumn
								{...overrideInfo}
								setter={header.setter}
								childSetter={header.child?.setter}
								type={header.type}
							/>
						);
					}

					try {
						const rawValue = info.getValue() as any;
						const value = (isSubRow && header.child)
							? (typeof header.child.id === "function" ? header.child.id(row) : row[header.child.id as any])
							: rawValue;

						const funcStyle = activeHeader?.styleFn
							? activeHeader?.styleFn(row)
							: undefined;

						const textColor = activeHeader?.color
							? typeof activeHeader.color === "function"
								? activeHeader.color(row)
								: activeHeader.color
							: undefined;

						return (
							<span
								style={{
									...activeHeader.style,
									width: activeHeader.width,
									...(funcStyle as any),
									...(textColor ? { color: textColor as any } : {}),
								}}
							>
								{activeHeader.formatter
									? (activeHeader as any).formatter(value)
									: value}
							</span>
						);
					} catch (error: any) {
						console.error(`[CRITICAL] Error rendering column ${header.key}:`, error);
						return <span style={{ color: "red" }}>{SBTranslate("error") || "ERROR"}</span>;
					}
				},
			} as DisplayColumnDef<T, unknown>);
		});

		if (index === firstRenderedGroup) columns = [...selectableColumn, ...columns!];

		return columnHelper.group({
			id: column.key ? `${column.key}_group` : `${column.name}_group`,
			header: column.name,
			hideToggle: column.hideToggle,
			cell: (info: any) => info.getValue(),
			columns,
		} as any);
	});
}

interface ColumnsAdapterProps<T> {
	columns: SBTableColumn<T>[];
	rows: T[];
	hasSelection?: boolean;
}
